/**
 * Карточка базы знаний (`/knowledge/<id>`), вкладки `?tab=`: card — текст, правка с версией, замечания
 * текстом или голосом; proposals — предложения модели с разницей (принять / поправить и принять /
 * отклонить); questions — вопросы клиентов, на которые отвечает карточка: у каждого замечание к ответу
 * на него (`kb_comment.kb_question_id`, быстрые заготовки) и случаи из диалогов; history — история с откатом.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { ContentDiff } from "@/components/knowledge/ContentDiff";
import { Hits } from "@/components/knowledge/Hits";
import { useVoice, VoiceControl, voiceBusy } from "@/components/voice/VoiceInput";
import { ApiFailure } from "@/lib/api";
import {
  accept,
  addComment,
  answerVoiceUrl,
  archive,
  AUTHORS,
  type CardDetail,
  type Comment,
  COMMENT_STATUS,
  KINDS,
  loadCard,
  loadMeta,
  loadQcBasis,
  type Proposal,
  propose,
  reject,
  retrySync,
  rollback,
  saveCard,
  type Version,
} from "@/lib/knowledge";
import { uploadVoice } from "@/lib/voice";

export const box = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
export const btn =
  "min-h-9 rounded-[var(--radius-ui)] border border-border px-3 py-1.5 text-[12.5px] text-fg-muted hover:text-fg disabled:opacity-50";
export const btnMain =
  "min-h-9 rounded-[var(--radius-ui)] bg-accent px-3 py-1.5 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-50";
export const input =
  "w-full rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-2 text-[16px] text-fg md:text-[13px]";

const chip = "rounded-full border border-border px-2.5 py-1 text-[12px] text-fg-muted hover:border-accent hover:text-fg";
const btnSmall = "min-h-7 rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] text-fg-muted hover:text-fg";
const tabCls = (on: boolean) =>
  `shrink-0 rounded-full border px-3 py-1 text-[12.5px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;

export function errText(e: unknown): string {
  return e instanceof ApiFailure ? e.message : e instanceof Error ? e.message : "Ошибка";
}

export function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`;
}

export function SyncBadge({ c, onRetry }: { c: CardDetail | { sync: CardDetail["sync"] }; onRetry?: (() => void) | undefined }) {
  const s = c.sync;
  const tone = s.state === "failed" ? "text-err" : s.label === "в поиске актуальна" ? "text-ok" : "text-warn";
  return (
    <span className={`text-[11.5px] ${tone}`} title={s.error || undefined}>
      LightRAG: {s.label}
      {onRetry && s.state === "failed" && (
        <button type="button" onClick={onRetry} className="ml-2 underline">
          повторить
        </button>
      )}
    </span>
  );
}

function Editor({ c, topics, onDone }: { c: CardDetail; topics: string[]; onDone: () => void }) {
  const [title, setTitle] = useState(c.title);
  const [body, setBody] = useState(c.body);
  const [topic, setTopic] = useState(c.topic);
  const [kind, setKind] = useState(c.kind);
  const [reason, setReason] = useState("");
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => saveCard(c.id, { title, body, topic, kind, location_id: c.location_id }, c.version, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb"] });
      onDone();
    },
  });
  return (
    <div className="space-y-2">
      <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className={`${input} min-h-48`} value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <select className={`${input} w-auto`} value={topic} onChange={(e) => setTopic(e.target.value)}>
          {topics.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <select className={`${input} w-auto`} value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(KINDS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>
      <input className={input} placeholder="Почему меняете (видно в истории)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="flex gap-2">
        <button type="button" className={btnMain} disabled={m.isPending || !body.trim()} onClick={() => m.mutate()}>
          Сохранить как v{c.version + 1}
        </button>
        <button type="button" className={btn} onClick={onDone}>
          Отмена
        </button>
      </div>
      {m.error && <div className="text-[12px] text-err">{errText(m.error)}</div>}
    </div>
  );
}

/** Быстрые замечания к ответу на вопрос клиента — кладут текст в поле, сохраняет человек. */
const БЫСТРЫЕ = [
  "Карточка не отвечает на этот вопрос",
  "Ответ на этот вопрос неточный или устарел",
  "Вопрос не к этой карточке",
  "Сюда нужны живые данные, а не текст",
];

function CommentBox({ c, questionId, onSaved }: { c: CardDetail; questionId?: number; onSaved?: () => void }) {
  const [text, setText] = useState("");
  const qc = useQueryClient();
  /** Сохранили `sent`: поле чистим, только если в нём всё ещё он — дописанное за время загрузки остаётся. */
  const done = (sent: string) => {
    setText((cur) => (cur.trim() === sent ? "" : cur));
    void qc.invalidateQueries({ queryKey: ["kb"] });
    onSaved?.();
  };
  const m = useMutation({
    mutationFn: (sent: string) => addComment(c.id, sent, c.version, questionId),
    onSuccess: (_, sent) => done(sent),
  });
  /** Голос — сразу замечанием (с набранным текстом, если есть): текст записи допишет фон. */
  const voice = useVoice({
    onDone: async (blob, seconds) => {
      const sent = text.trim(); // текст на момент «Стоп» (onDone у useVoice — свежий)
      const rec = await uploadVoice(blob, seconds);
      await addComment(c.id, sent, c.version, questionId, rec.id);
      done(sent);
    },
  });
  const кВопросу = questionId !== undefined;
  return (
    <div className="space-y-2">
      {кВопросу && (
        <div className="flex flex-wrap gap-1.5">
          {БЫСТРЫЕ.map((t) => (
            <button key={t} type="button" className={chip} onClick={() => setText((old) => (old.trim() ? `${old.trim()}. ${t}` : t))}>
              {t}
            </button>
          ))}
        </div>
      )}
      <textarea
        className={`${input} min-h-24`}
        placeholder={
          кВопросу
            ? "Что не так в ответе на этот вопрос — заготовкой выше, текстом или голосом"
            : "Что поправить в карточке — текстом или надиктуйте голосом (сохранится сразу)"
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        {/* Один путь сохранения: пока голос пишется или уходит — текстом не сохраняем, и наоборот. */}
        <VoiceControl voice={voice} disabled={m.isPending} sentText="Замечание голосом сохранено — текст появится сам" />
        <button
          type="button"
          className={btnMain}
          disabled={!text.trim() || m.isPending || voiceBusy(voice.v)}
          onClick={() => m.mutate(text.trim())}
        >
          Сохранить замечание
        </button>
      </div>
      {m.error && <div className="text-[12px] text-err">{errText(m.error)}</div>}
    </div>
  );
}

function ProposalCard({ p, c, canEdit }: { p: Proposal; c: CardDetail; canEdit: boolean }) {
  const [edit, setEdit] = useState(false);
  const [body, setBody] = useState(p.body);
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ["kb"] });
  const ok = useMutation({ mutationFn: () => accept(p.id, edit ? { body } : undefined), onSuccess: done });
  const no = useMutation({ mutationFn: () => reject(p.id), onSuccess: done });
  const устарело = p.base_version !== c.version;
  const неМенять = p.result === "recommend_reject";
  return (
    <div className={`${box} space-y-2 p-3`} data-annot={`предложение ${p.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[12px]">
        <span className="font-medium text-fg">
          Предложение P{p.id} · к v{p.base_version} · {p.source === "comments" ? "по замечаниям" : p.source === "model" ? "из вопросов клиентов" : p.source}
        </span>
        <span className="text-fg-faint">{when(p.date)}</span>
      </div>
      {p.reason && <div className="text-[12.5px] text-fg-muted">{p.reason}</div>}
      {устарело && <div className="text-[12px] text-warn">Карточку изменили после сборки — принять не получится, соберите заново.</div>}
      {неМенять && !edit && <div className="text-[12.5px] text-fg">Модель считает, что карточку менять не нужно.</div>}
      {!неМенять && p.title !== c.title && <ContentDiff label="заголовок" before={c.title} after={p.title} />}
      {edit ? (
        <textarea className={`${input} min-h-48`} value={body} onChange={(e) => setBody(e.target.value)} />
      ) : неМенять ? null : (
        <ContentDiff label="текст" before={c.body} after={p.body} />
      )}
      {p.source === "qc" && p.status === "new" && (
        <div className="text-[12px] text-fg-muted">
          Из ченджлога контроля качества — решение в{" "}
          <Link to="/qc" className="underline">
            «Контроль качества»
          </Link>
          .
        </div>
      )}
      {canEdit && p.status === "new" && p.source !== "qc" && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnMain} disabled={ok.isPending || устарело} onClick={() => ok.mutate()}>
            {edit ? "Принять с моей правкой" : неМенять ? "Согласен, не менять" : "Принять"}
          </button>
          <button type="button" className={btn} onClick={() => setEdit((v) => !v)}>
            {edit ? (неМенять ? "Отмена" : "Показать разницу") : неМенять ? "Поправить самому" : "Поправить текст"}
          </button>
          <button type="button" className={btn} disabled={no.isPending} onClick={() => no.mutate()}>
            {неМенять ? "Отклонить замечания" : "Отклонить"}
          </button>
        </div>
      )}
      {!canEdit && p.status === "new" && p.source !== "qc" && <div className="text-[12px] text-fg-faint">Предложение ждёт редактора.</div>}
      {(ok.error || no.error) && <div className="text-[12px] text-err">{errText(ok.error ?? no.error)}</div>}
    </div>
  );
}

function History({ c, canEdit }: { c: CardDetail; canEdit: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  const qc = useQueryClient();
  const back = useMutation({
    mutationFn: (n: number) => rollback(c.id, n, c.version),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["kb"] }),
  });
  const prev = (v: Version) => c.versions.find((x) => x.number === v.number - 1);
  return (
    <div className="divide-y divide-border">
      {c.versions.map((v) => (
        <div key={v.number} className="py-2">
          <button type="button" className="flex w-full flex-wrap items-baseline gap-x-2 text-left text-[12.5px]" onClick={() => setOpen(open === v.number ? null : v.number)}>
            <span className="font-medium text-fg">v{v.number}</span>
            <span className="text-fg-muted">{AUTHORS[v.author_kind] ?? v.author_kind}</span>
            <span className="text-fg-faint">{when(v.date)}</span>
            <span className="min-w-0 flex-1 truncate text-fg-muted">{v.reason}</span>
          </button>
          {open === v.number && (
            <div className="mt-2 space-y-2">
              <ContentDiff label={prev(v) ? `разница с v${v.number - 1}` : "первая версия"} before={prev(v)?.body ?? ""} after={v.body} />
              {canEdit && v.number !== c.version && (
                <button type="button" className={btn} disabled={back.isPending} onClick={() => back.mutate(v.number)}>
                  Вернуть текст v{v.number} (станет v{c.version + 1})
                </button>
              )}
              {back.error && <div className="text-[12px] text-err">{errText(back.error)}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CommentRow({ x, withQuestion = true }: { x: Comment; withQuestion?: boolean }) {
  return (
    <div className="py-2 text-[12.5px]">
      <div className="flex flex-wrap gap-x-2 text-fg-faint">
        <span className="text-fg-muted">{x.author || "сотрудник"}</span>
        <span>{when(x.date)}</span>
        <span>к v{x.version}</span>
        <span>{x.voice ? "🎤 голосом" : x.source === "tg" ? "из «AI КЦ»" : ""}</span>
        <span className={x.status === "new" ? "text-warn" : ""}>{COMMENT_STATUS[x.status] ?? x.status}</span>
      </div>
      {withQuestion && x.question && <div className="text-[12px] text-fg-muted">к вопросу «{x.question}»</div>}
      <div className="whitespace-pre-wrap break-words text-fg">{x.text || (x.status === "waiting_voice" ? "Расшифровывается…" : "…")}</div>
      {x.voice_id && <audio controls preload="none" src={answerVoiceUrl(x.voice_id)} className="mt-1 h-8 w-full max-w-sm" />}
    </div>
  );
}

type Открыто = { id: number; what: "comment" | "hits" } | null;

function Questions({ c }: { c: CardDetail }) {
  const [open, setOpen] = useState<Открыто>(null);
  const toggle = (id: number, what: "comment" | "hits") => setOpen((o) => (o?.id === id && o.what === what ? null : { id, what }));
  const on = (id: number, what: "comment" | "hits") => open?.id === id && open.what === what;
  if (!c.questions_list.length) return <div className="text-[12.5px] text-fg-muted">К карточке ещё не привязан ни один вопрос.</div>;
  return (
    <div className="divide-y divide-border">
      {c.questions_list.map((x) => {
        const замечания = c.comments_list.filter((m) => m.question_id === x.id);
        const новых = замечания.filter((m) => m.status === "new").length;
        return (
          <div key={x.id} className="py-2" data-annot={`вопрос ${x.id}`}>
            <div className="flex items-start gap-2 text-[12.5px]">
              <span className="w-8 shrink-0 text-right tabular-nums text-fg-muted" title="в скольких диалогах звучал">
                {x.hits}
              </span>
              <span className="min-w-0 flex-1 text-fg">
                {x.text}
                {замечания.length > 0 && (
                  <span className={`ml-2 text-[11.5px] ${новых ? "text-warn" : "text-fg-faint"}`}>
                    замечаний {замечания.length}
                    {новых ? ` · новых ${новых}` : ""}
                  </span>
                )}
              </span>
              <div className="flex shrink-0 gap-1.5">
                <button type="button" className={`${btnSmall} ${on(x.id, "hits") ? "border-accent text-fg" : ""}`} onClick={() => toggle(x.id, "hits")}>
                  Диалоги
                </button>
                <button type="button" className={`${btnSmall} ${on(x.id, "comment") ? "border-accent text-fg" : ""}`} onClick={() => toggle(x.id, "comment")}>
                  + Замечание
                </button>
              </div>
            </div>
            {on(x.id, "comment") && (
              <div className="ml-10 mt-2">
                <CommentBox c={c} questionId={x.id} onSaved={() => setOpen(null)} />
              </div>
            )}
            {on(x.id, "hits") && (
              <div className="ml-10">
                <Hits id={x.id} days={90} cards={false} />
              </div>
            )}
            {замечания.length > 0 && (
              <div className="ml-10 divide-y divide-border">
                {замечания.map((m) => (
                  <CommentRow key={m.id} x={m} withQuestion={false} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function QcBasisBlock({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["kb", "qc", id], queryFn: () => loadQcBasis(id), retry: false });
  const всего = (q.data?.versions ?? []).reduce((n, v) => n + v.remarks.length + v.confirmed.length, 0);
  if (!q.data || !всего) return null;
  return (
    <section className={`${box} p-3`}>
      <h2 className="mb-2 text-[13px] font-semibold text-fg">Основания из контроля качества · {всего}</h2>
      <div className="space-y-2 text-[12.5px]">
        {q.data.versions
          .filter((v) => v.remarks.length || v.confirmed.length)
          .map((v) => (
            <div key={v.number}>
              <div className="text-[11.5px] text-fg-faint">v{v.number}</div>
              {[...v.remarks.map((r) => ({ r, ok: false })), ...v.confirmed.map((r) => ({ r, ok: true }))].map(({ r, ok }) => (
                <div key={r.id} className="text-fg">
                  <span className="text-fg-muted">{r.author}</span> {ok ? "· правило подтверждено" : ""}: {r.text || r.transcript}
                  {r.chat_id && (
                    <Link to={`/chats/${r.chat_id}`} className="ml-1 text-fg-muted underline">
                      диалог
                    </Link>
                  )}
                </div>
              ))}
            </div>
          ))}
      </div>
    </section>
  );
}

/** `id` — карточка в панели справа (`CardPanel`): вкладка — своя, не в адресе (адрес принадлежит списку). */
export default function KnowledgeCardPage({ id: inPanel }: { id?: number } = {}) {
  const fromUrl = Number(useParams().cardId);
  const id = inPanel ?? fromUrl;
  const meta = useQuery({ queryKey: ["kb", "meta"], queryFn: loadMeta });
  const q = useQuery({
    queryKey: ["kb", "card", id],
    queryFn: () => loadCard(id),
    enabled: Number.isFinite(id),
    // замечание голосом ждёт расшифровки — подтягиваем, пока текст не придёт
    refetchInterval: (query) => (query.state.data?.comments_list.some((x) => x.status === "waiting_voice") ? 3000 : false),
  });
  const [editing, setEditing] = useState(false);
  const [params, setParams] = useSearchParams();
  const [panelTab, setPanelTab] = useState("card");
  // в панели вкладка не сбрасывается сменой карточки сама — сбрасываем
  useEffect(() => setPanelTab("card"), [inPanel]);
  const tab = inPanel ? panelTab : (params.get("tab") ?? "card");
  const go = (t: string) => (inPanel ? setPanelTab(t) : setParams(t === "card" ? {} : { tab: t }, { replace: true }));
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["kb"] });
  const build = useMutation({ mutationFn: () => propose(id), onSuccess: refresh });
  const retry = useMutation({ mutationFn: () => retrySync(id), onSuccess: refresh });
  const toArchive = useMutation({
    mutationFn: () => archive(id, q.data?.version ?? 0, "устарела"),
    onSuccess: refresh,
  });
  const canEdit = meta.data?.can_edit ?? false;
  if (q.isLoading) return <div className="p-4 text-fg-muted">Загрузка…</div>;
  if (q.error || !q.data) return <div className="p-4 text-err">{errText(q.error)}</div>;
  const c = q.data;
  const новых = c.comments_list.filter((x) => x.status === "new").length;
  const ждут = c.proposals_list.filter((p) => p.status === "new");
  return (
    <div className={`mx-auto max-w-3xl space-y-4 ${inPanel ? "p-4" : "p-3 md:p-5"}`}>
      <div>
        {inPanel ? (
          <Link to={`/knowledge/${c.id}`} className="text-[12px] text-fg-muted hover:text-fg">
            открыть страницей ↗
          </Link>
        ) : (
          <Link to="/knowledge?tab=cards" className="text-[12px] text-fg-muted hover:text-fg">
            ← все карточки
          </Link>
        )}
        <h1 className="mt-1 text-[18px] font-semibold text-fg">{c.title}</h1>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-fg-muted">
          <span>#{c.id} · v{c.version}</span>
          <span>{c.topic}</span>
          <span>{KINDS[c.kind] ?? c.kind}</span>
          {c.status !== "active" && <span className="text-warn">{c.status === "merged" ? `слита в #${c.merged_into}` : "в архиве"}</span>}
          {c.origin && <span className="text-fg-faint">{c.origin.startsWith("kc_faq") ? "из справочника kc_faq" : c.origin}</span>}
          <SyncBadge c={c} onRetry={canEdit ? () => retry.mutate() : undefined} />
        </div>
      </div>

      <nav className="flex gap-1.5 overflow-x-auto">
        {(
          [
            ["card", "Карточка", новых, true],
            ["proposals", "Предложения модели", ждут.length, true],
            ["questions", "Вопросы клиентов", c.questions_list.length, false],
            ["history", "История", c.versions.length, false],
          ] as [string, string, number, boolean][]
        ).map(([k, label, n, важно]) => (
          <button key={k} type="button" className={tabCls(tab === k)} onClick={() => go(k)}>
            {label}
            {n > 0 && <span className={`ml-1.5 tabular-nums ${важно ? "text-warn" : "text-fg-faint"}`}>{n}</span>}
          </button>
        ))}
      </nav>

      {tab === "card" && (
        <>
          <section className={`${box} p-3`}>
            {editing ? (
              <Editor c={c} topics={meta.data?.topics ?? []} onDone={() => setEditing(false)} />
            ) : (
              <>
                <div className="whitespace-pre-wrap break-words text-[14px] leading-relaxed text-fg">{c.body}</div>
                {canEdit && c.status === "active" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" className={btn} onClick={() => setEditing(true)}>
                      Править
                    </button>
                    <button type="button" className={btn} disabled={toArchive.isPending} onClick={() => confirm("Убрать карточку в архив? Она пропадёт из поиска.") && toArchive.mutate()}>
                      В архив
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {ждут.length > 0 && (
            <button type="button" className={`${box} block w-full p-3 text-left text-[12.5px] text-fg-muted hover:text-fg`} onClick={() => go("proposals")}>
              Ждут решения: предложения модели · <span className="text-warn">{ждут.length}</span> →
            </button>
          )}

          <section className={`${box} space-y-3 p-3`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-[13px] font-semibold text-fg">Замечания</h2>
              {canEdit && новых > 0 && (
                <button type="button" className={btnMain} disabled={build.isPending} onClick={() => build.mutate()}>
                  {build.isPending ? "Модель собирает…" : `Собрать предложение из ${новых}`}
                </button>
              )}
            </div>
            {build.error && <div className="text-[12px] text-err">{errText(build.error)}</div>}
            <CommentBox c={c} />
            <div className="divide-y divide-border">
              {c.comments_list.map((x) => (
                <CommentRow key={x.id} x={x} />
              ))}
            </div>
          </section>

          <QcBasisBlock id={c.id} />
        </>
      )}

      {tab === "proposals" &&
        (ждут.length ? (
          <section className="space-y-2">
            {ждут.map((p) => (
              <ProposalCard key={p.id} p={p} c={c} canEdit={canEdit} />
            ))}
          </section>
        ) : (
          <div className="text-[12.5px] text-fg-muted">Новых предложений нет.</div>
        ))}

      {tab === "questions" && (
        <section className={`${box} p-3`}>
          <div className="mb-2 text-[12px] text-fg-muted">
            Вопросы клиентов, на которые отвечает карточка; число — в скольких диалогах звучал. «+ Замечание» — что не так в ответе
            именно на этот вопрос: вопрос уйдёт вместе с замечанием в предложение.
          </div>
          <Questions c={c} />
        </section>
      )}

      {tab === "history" && (
        <section className={`${box} p-3`}>
          <History c={c} canEdit={canEdit} />
        </section>
      )}
    </div>
  );
}
